import { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, TrashIcon, ClipboardIcon, CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { userSession } from '../utils/userSession.js';
import ConfirmDialog from './ConfirmDialog.js';

const API_BASE = 'https://api.driftcal.net';

function formatWhen(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export default function UserApiKeys() {
  const user = userSession.getUser();
  const sessionToken = user?.sessionToken;
  const [consumers, setConsumers] = useState([]);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [issued, setIssued] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [open, setOpen] = useState(false);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-session-token': sessionToken
  }), [sessionToken]);

  const load = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const response = await fetch(`${API_BASE}/v1/me/consumers`, { headers: headers() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load API keys');
      }
      setConsumers(data.consumers || []);
      setError('');
    } catch (err) {
      console.error('Failed to load API keys:', err);
      setConsumers([]);
      setError('');
    }
  }, [sessionToken, headers]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRotate = async (consumerId) => {
    setBusyId(`rotate-${consumerId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/me/consumers/${consumerId}/rotate`, {
        method: 'POST',
        headers: headers()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to rotate key');
      }
      setIssued(data);
      setCopied(false);
      setOpen(true);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to rotate key');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const handleDelete = async (consumerId) => {
    setBusyId(`delete-${consumerId}`);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/v1/me/consumers/${consumerId}/revoke`, {
        method: 'POST',
        headers: headers()
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete key');
      }
      if (issued?.consumerId === consumerId) {
        setIssued(null);
      }
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete key');
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const copyKey = async () => {
    if (!issued?.apiKey) return;
    try {
      await navigator.clipboard.writeText(issued.apiKey);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!sessionToken || (consumers.length === 0 && !issued)) {
    return null;
  }

  const keyCount = consumers.length;

  return (
    <div className="mb-6 overflow-hidden rounded-lg border bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
      >
        <h2 className="text-lg font-semibold text-gray-900">API keys</h2>
        <span className="flex items-center gap-2 text-sm text-gray-500">
          {keyCount} {keyCount === 1 ? 'key' : 'keys'}
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {open && (
        <div className="border-t px-4 pb-4 pt-3">
          <p className="mb-4 text-sm text-gray-600">
            Keys for this account. The full key is only shown when you rotate it.
          </p>

          {error && (
            <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          {issued?.apiKey && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-950">New key — copy it now</p>
              <p className="mt-1 text-xs text-amber-900">
                The previous key for {issued.origin} no longer works.
              </p>
              <div className="mt-2 flex items-start gap-2">
                <code className="flex-1 break-all rounded-md border border-amber-200 bg-white px-3 py-2 text-xs text-slate-900">
                  {issued.apiKey}
                </code>
                <button
                  type="button"
                  onClick={copyKey}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-700"
                >
                  {copied ? <CheckCircleIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {consumers.map((consumer) => (
              <div key={consumer.id} className="rounded-lg border p-3">
                <div className="break-all font-mono text-sm text-gray-800">{consumer.key_prefix}</div>
                <div className="mt-1 text-xs text-gray-500">
                  {consumer.origin} · last used {formatWhen(consumer.last_used_at)}
                </div>
                <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                  <button
                    type="button"
                    disabled={Boolean(busyId)}
                    onClick={() => setConfirm({ type: 'rotate', consumer })}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    {busyId === `rotate-${consumer.id}` ? 'Rotating…' : 'Rotate'}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busyId)}
                    onClick={() => setConfirm({ type: 'delete', consumer })}
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {busyId === `delete-${consumer.id}` ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.type === 'rotate') handleRotate(confirm.consumer.id);
          if (confirm.type === 'delete') handleDelete(confirm.consumer.id);
        }}
        title={confirm?.type === 'rotate' ? 'Rotate API key' : 'Delete API key'}
        message={
          confirm?.type === 'rotate'
            ? `Replace the key for ${confirm.consumer.origin}? The current key will stop working immediately.`
            : confirm
              ? `Delete the key for ${confirm.consumer.origin}? Calls with that key will start failing immediately.`
              : ''
        }
        confirmLabel={confirm?.type === 'rotate' ? 'Rotate' : 'Delete'}
      />
    </div>
  );
}
